/**
 * CANONICAL SUPABASE CLIENT
 * This is the single source of truth for the Supabase client configuration.
 * Other files (services/supabase.ts, config/supabase.ts) re-export from here.
 */
import { createClient } from '@supabase/supabase-js';

// Get environment variables with fallback to production Supabase
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://lkmrsvvgisdthvlqjhdk.supabase.co';
// Production anon key for lkmrsvvgisdthvlqjhdk.supabase.co
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxrbXJzdnZnaXNkdGh2bHFqaGRrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU4MTE2NDUsImV4cCI6MjA3MTM4NzY0NX0.zyv2cq1xhN6XlhVLlPSvC7eKJKXEA2KLvOCJbW9Wzdc';

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Auth helper functions
export const signIn = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Sign in error:', error);
    return { data: null, error };
  }
};

export const signUp = async (email: string, password: string, metadata?: any) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Sign up error:', error);
    return { data: null, error };
  }
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Sign out error:', error);
    return { error };
  }
};

export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return { user, error: null };
  } catch (error) {
    console.error('Get current user error:', error);
    return { user: null, error };
  }
};

// Database helper functions
export const fetchCompanies = async () => {
  try {
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Fetch companies error:', error);
    return { data: null, error };
  }
};

export const fetchEmployees = async (companyId?: string) => {
  try {
    let query = supabase
      .from('employees')
      .select('*')
      .order('created_at', { ascending: false });

    if (companyId) {
      query = query.eq('company_id', companyId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Fetch employees error:', error);
    return { data: null, error };
  }
};

// Realtime subscriptions
export const subscribeToTable = (
  table: string,
  callback: (payload: any) => void,
  filter?: { column: string; value: any }
) => {
  let subscription = supabase
    .channel(`${table}-changes`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: table,
        filter: filter ? `${filter.column}=eq.${filter.value}` : undefined
      },
      callback
    )
    .subscribe();

  return subscription;
};

// Storage helpers
export const uploadFile = async (bucket: string, path: string, file: File) => {
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file);

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Upload file error:', error);
    return { data: null, error };
  }
};

export const getFileUrl = (bucket: string, path: string) => {
  const { data } = supabase.storage
    .from(bucket)
    .getPublicUrl(path);

  return data?.publicUrl;
};

// Export types
export type { User } from '@supabase/supabase-js';